import React, { forwardRef, InputHTMLAttributes, useMemo } from "react";

import * as Styled from "./styles";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
}

let fallbackIdCounter = 0;

/**
 * Floating-label input. Plays well with `react-hook-form` via `register`,
 * and with any other ref-forwarding form lib. The label floats whenever the
 * field is focused or holds a value — driven by the `:placeholder-shown`
 * pseudo-class on the native input.
 */
const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, id, ...rest }, ref) => {
    // Fallback only when neither `id` nor `name` is provided — labels can
    // hold spaces/accents/duplicates, so they can't be used as DOM ids.
    const fallbackId = useMemo(() => {
      fallbackIdCounter += 1;

      return `input-${fallbackIdCounter}`;
    }, []);
    const inputId = id ?? rest.name ?? fallbackId;
    const errorId = `${inputId}-error`;

    return (
      <Styled.Container hasError={!!error}>
        <Styled.Field>
          <input
            id={inputId}
            ref={ref}
            placeholder=" "
            aria-invalid={!!error}
            aria-describedby={error ? errorId : undefined}
            {...rest}
          />
          <Styled.Label htmlFor={inputId}>{label}</Styled.Label>
        </Styled.Field>
        {error && <Styled.Error id={errorId} role="alert">{error}</Styled.Error>}
      </Styled.Container>
    );
  }
);

Input.displayName = "Input";

Input.defaultProps = {
  error: undefined
};

export default Input;
